import { Injector, Service, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxAmount,
  OnyxConfirmModalData,
  OnyxFilter,
  OnyxIconName,
  OnyxOptional,
  OnyxTime,
} from '@onyx/angular';
import {
  ActionHelper,
  AmountHelper,
  DropdownHelper,
  OnyxConfirmModalComponent,
  OnyxDatePipe,
  OnyxFilterOperator,
  OnyxMapRoutePointStatus,
  OnyxModalService,
  OnyxTimePipe,
  OnyxToastService,
  type OnyxAddress,
  type OnyxModalResult,
  type OnyxOptionsGroup,
  type OnyxRoute,
  type OnyxTimeRange,
} from '@onyx/angular';
import { chain, isArray, isString, sumBy, uniq } from 'lodash';
import { DateTime, Duration } from 'luxon';
import { EMPTY, tap, type Observable } from 'rxjs';
import { AuthStore } from '../../../../auth/common/stores/auth.store';
import { DAY_TIME_RANGE } from '../../../../common/constants/common/day-time-range';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { GoodsSizeType } from '../../../../common/enums/goods-size-type';
import { InvoiceType } from '../../../../common/enums/invoice-type';
import type { VatRate } from '../../../../common/enums/vat-rate';
import { TelematicsRoutePointFactory } from '../../../../common/factories/telematics/telematics-route-point.factory';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import { DeadlineHelper } from '../../../../common/helpers/deadline.helper';
import { NavigationHelper } from '../../../../common/helpers/navigation.helper';
import { RouteHelper } from '../../../../common/helpers/route.helper';
import type { BusinessHours } from '../../../../common/interfaces/common/business-hours';
import type { Point } from '../../../../common/interfaces/common/point';
import type { TelematicsOrder } from '../../../../common/interfaces/telematics/telematics-order';
import type {
  TelematicsOrderRoutePointDto,
  TelematicsRoutePoint,
} from '../../../../common/interfaces/telematics/telematics-route-point';
import type { TelematicsRoutePointOrder } from '../../../../common/interfaces/telematics/telematics-route-point-order';
import { TelematicsRoutePointStatus } from '../../../../common/interfaces/telematics/telematics-vehicle-point-status';
import type { HelperOptions } from '../../../../common/interfaces/utilities/helper-options';
import { BusinessHoursPipe } from '../../../../common/pipes/business-hours.pipe';
import { DriverHelper } from '../../../drivers/common/helpers/driver.helper';
import type {
  SimplifiedDrivers,
  SimplifiedFleet,
} from '../../../fleet/common/interfaces/fleet';
import type { ShippingType } from '../../../management-panel/contractors/common/interfaces/contractor';
import type { DictionariesNamesTranslation } from '../../../management-panel/dictionaries/common/components/forms/dictionaries-names-translation-form/dictionaries-names-translation-form.component';
import { EmptyDistanceStrategy } from '../../../management-panel/engine-config/common/enums/empty-distance-strategy';
import type { OrderModalData } from '../../order-modal/order-modal.component';
import { OrderModalComponent } from '../../order-modal/order-modal.component';
import type { DriverReportModalFileData } from '../components/driver-report-modal/driver-report-modal.component';
import type { OrderCancelModalData } from '../components/order-cancel-modal/order-cancel-modal.component';
import { OrderCancelModalComponent } from '../components/order-cancel-modal/order-cancel-modal.component';
import type { OrderCategory } from '../enums/order-category';
import { OrderDispatch } from '../enums/order-dispatch';
import { OrderMissingAssignmentType } from '../enums/order-missing-assignment-type';
import { OrderPlanningMode } from '../enums/order-planning-mode';
import { OrderPointCategory } from '../enums/order-point-category';
import { OrderPointStatus } from '../enums/order-point-status';
import type { OrderPointType } from '../enums/order-point-type';
import { OrderProposalType } from '../enums/order-proposal-type';
import { OrderStatus } from '../enums/order-status';
import { OrderTimeWindowType } from '../enums/order-time-window-type';
import { OrderTransitPointTimeWindowType } from '../enums/order-transit-point-time-window-type';
import { OrdersQueryParamKey } from '../enums/orders-query-param-key';
import { OrdersRoute } from '../enums/orders-route';
import { OrdersRouterStateKey } from '../enums/orders-router-state-key';
import type {
  AssignationData,
  OrderPayment,
  OrderProposal,
  SimplifiedOrder,
} from '../interfaces/order';
import type {
  OrderLoadingPoint,
  OrderPoint,
  OrderUnloadingPoint,
} from '../interfaces/order-point';
import type {
  CompletedReport,
  InProgressReport,
} from '../interfaces/order-point-driver-reports';
import { OrdersService } from '../services/orders.service';
import type { Order } from './../interfaces/order';
import type { OrderLoadingGood } from './../interfaces/order-point';
import type { OrderLoadingPointGoodForm } from './order-form.helper';

export interface OrderSettlementData {
  net: Pick<OnyxAmount, 'value' | 'currency'>;
  gross: Pick<OnyxAmount, 'value' | 'currency'>;
  vatRate: Pick<OnyxAmount, 'value' | 'currency'> & { vatRate: VatRate };
}

type OrderCrew = Pick<
  AssignationData,
  'vehicle' | 'trailer' | 'primaryDriver' | 'secondaryDriver'
>;

interface OrderPointDurationData {
  timestamp: string;
  date: string;
  time: OnyxTime;
  isEstimated: boolean;
}

type OrderBaseReferenceItem = {
  category: 'order' | 'point' | 'good';
  referenceNumber: string;
};

export type OrderReferenceItem =
  | (OrderBaseReferenceItem & {
      category: 'order';
    })
  | (OrderBaseReferenceItem & {
      category: 'point';
      type: OrderPointType;
      typeIndex: number;
    })
  | (OrderBaseReferenceItem & {
      category: 'good';
      names: DictionariesNamesTranslation;
    });

export const ORDER_HEADER_ACTIONS = [
  'share',
  'showOnGantt',
  'showOnMap',
] as const;

@Service()
export class OrderHelper {
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly modalService = inject(OnyxModalService);
  private readonly toastService = inject(OnyxToastService);
  private readonly ordersService = inject(OrdersService);
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly timePipe = inject(OnyxTimePipe);
  private readonly businessHoursPipe = inject(BusinessHoursPipe);
  private readonly commonHelper = inject(CommonHelper);
  private readonly injector = inject(Injector);

  public getOptions(
    data: Order | TelematicsOrder,
    options: HelperOptions,
    exclude: readonly (typeof ORDER_HEADER_ACTIONS)[number][] = [],
  ): OnyxOptionsGroup<() => void>[] {
    const order = OrderHelper.isTelematicsOrder(data) ? data.order : data;
    if (!order) return [];

    const hasPlannedSwap = OrderHelper.hasPlannedSwap(order);
    const hasEdit = OrderHelper.hasEdit(order);
    const planRoute = this.getPlanRouteState(order);
    const editLock = OrderHelper.getEditLock(order);

    const getDuplicateOption = (
      order: Order | TelematicsRoutePointOrder,
      options: HelperOptions,
    ) =>
      ({
        name: `${this.translateService.instant('labels.duplicate')}...`,
        value: () => this.duplicate(order, options),
        leftIcon: { name: 'copy-outlined', size: 20 },
      }) as const;

    const getLockedFields = (tooltip: string) =>
      ({
        rightIcon: { name: 'locked-outlined', size: 16 },
        rightIconColor: 'gray-alt',
        disabled: true,
        rightIconTooltip: tooltip,
      }) as const;

    return [
      {
        id: '0.draft',
        options: [
          ...(OrderHelper.isSearching(order)
            ? [
                {
                  name: 'orders.draft.moveToDraft',
                  value: () => this.moveToDraft(order, options).subscribe(),
                  leftIcon: { name: 'undo-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(OrderHelper.isPausedDraft(order)
            ? [
                {
                  name: 'orders.draft.resumeSearch',
                  value: () => this.resumeSearch(order, options).subscribe(),
                  leftIcon: { name: 'search-outlined', size: 20 },
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '1.default',
        options: [
          ...(hasEdit
            ? [
                {
                  name: `${this.translateService.instant('labels.edit')}...`,
                  value: () => this.edit(order.uuid, { options }),
                  leftIcon: { name: 'edit-outlined', size: 20 },
                  ...(hasPlannedSwap &&
                    DropdownHelper.getErrorFields(
                      'labels.disabledUntilSwapCompleted',
                    )),
                  ...(editLock.disabled && getLockedFields(editLock.tooltip)),
                } as const,
                getDuplicateOption(order, options),
                ...(planRoute
                  ? [
                      {
                        name: `${this.translateService.instant('labels.planRoute')}...`,
                        value: () =>
                          this.edit(order.uuid, {
                            options,
                            params: { [OrdersQueryParamKey.PLAN_ROUTE]: true },
                          }),
                        leftIcon: { name: 'route-outlined', size: 20 },
                        ...(planRoute.disabled &&
                          (hasPlannedSwap
                            ? DropdownHelper.getErrorFields(planRoute.tooltip)
                            : getLockedFields(planRoute.tooltip))),
                      } as const,
                    ]
                  : []),
              ]
            : []),
        ],
      },
      {
        id: '2.actions',
        options: [
          ...(OrderHelper.isInactive(order)
            ? [getDuplicateOption(order, options)]
            : []),
          ...(!exclude.includes('share')
            ? [
                {
                  name: 'labels.share',
                  value: () => this.share(order),
                  leftIcon: { name: 'share-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(!exclude.includes('showOnGantt') && !OrderHelper.isInactive(order)
            ? [
                {
                  name: 'labels.showOnGantt',
                  value: () => this.showOnGantt(order, options),
                  leftIcon: { name: 'gantt-outlined', size: 20 },
                } as const,
              ]
            : []),
          ...(!exclude.includes('showOnMap') && OrderHelper.hasShowOnMap(data)
            ? [
                {
                  name: 'labels.showOnMap',
                  value: () => this.showOnMap(data, options),
                  leftIcon: { name: 'map-view-outlined', size: 20 },
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '3.vehicle',
        options: [
          ...(OrderHelper.isSearching(order) &&
          OrderHelper.hasFleetDispatch(order)
            ? [
                {
                  name: `${this.translateService.instant('labels.chooseVehicle')}...`,
                  value: () => {
                    this.openModal(order, {
                      planningMode: OrderPlanningMode.MANUAL,
                    });
                    CommonHelper.handleOptions(order, options);
                  },
                  leftIcon: { name: 'vehicle-outlined', size: 20 },
                } as const,
              ]
            : []),
        ],
      },
      {
        id: '4.delete',
        options: [
          ...(!OrderHelper.isFinished(order) && !OrderHelper.isDraft(order)
            ? [
                {
                  name: 'labels.revoke',
                  value: () =>
                    this.modalService.open<OrderCancelModalData>(
                      OrderCancelModalComponent,
                      order,
                    ),
                  leftIcon: { name: 'clear-outlined', size: 20 },
                  leftIconColor: 'red',
                } as const,
              ]
            : []),
          ...(OrderHelper.isDraft(order)
            ? [
                {
                  name: 'labels.delete',
                  value: () => this.delete(order, options),
                  leftIcon: { name: 'delete-outlined', size: 20 },
                  leftIconColor: 'red',
                } as const,
              ]
            : []),
          ...(order.status.value === OrderStatus.PAID
            ? [
                {
                  name: 'labels.archive',
                  value: () => this.archive(order.uuid, options),
                  leftIcon: { name: 'archives-outlined', size: 20 },
                  leftIconColor: 'red',
                } as const,
              ]
            : []),
        ],
      },
    ].filter((group) => group.options.length !== 0);
  }

  public openModal(
    order: OrderModalData['order'],
    options?: Omit<OrderModalData, 'order'>,
  ): OnyxModalResult<void> {
    return this.modalService.open<OrderModalData, void>(OrderModalComponent, {
      order,
      ...options,
    });
  }

  public getPlanRouteState(
    order: Order | TelematicsRoutePointOrder,
  ): { disabled: boolean; tooltip: string } | null {
    if (OrderHelper.isFinished(order)) return null;
    if (OrderHelper.hasPlannedSwap(order)) {
      return { disabled: true, tooltip: 'labels.disabledUntilSwapCompleted' };
    }

    const editLock = OrderHelper.getEditLock(order);
    if (editLock.disabled) return editLock;

    if (!this.hasRouteEdit(order)) {
      return { disabled: true, tooltip: 'labels.routeBlocked' };
    }

    return { disabled: false, tooltip: 'labels.planRoute' };
  }

  public hasRouteEdit(order: Order | TelematicsRoutePointOrder): boolean {
    if (!OrderHelper.hasEdit(order)) return false;
    if (OrderHelper.isFinished(order)) return false;
    if (!order.route.isBlocked) return true;

    const whitelist = chain(order)
      .thru((order) => [order.author, order.route.author])
      .map((author) => author.uuid)
      .uniq()
      .value();
    const employeeUuid = this.authStore.user()!.employeeUuid;

    return whitelist.includes(employeeUuid);
  }

  public edit(
    uuid: string,
    data: {
      options: HelperOptions;
      params?: Partial<{ [OrdersQueryParamKey.PLAN_ROUTE]: boolean }>;
    },
  ): void {
    const route = this.router.createUrlTree(
      [`${OrdersRoute.EDIT_ORDER.replace(':uuid', uuid)}`],
      { queryParams: data.params },
    );
    this.router.navigateByUrl(route, NavigationHelper.withOrigin(this.router));
    CommonHelper.handleOptions(uuid, data.options);
  }

  public share(order: SimplifiedOrder | TelematicsRoutePointOrder): void {
    ActionHelper.share(
      OrdersRoute.ORDER_CARD.replace(':uuid', order.uuid),
      this.toastService,
    );
  }

  public showOnGantt(
    order: SimplifiedOrder | TelematicsRoutePointOrder,
    options: HelperOptions,
  ): void {
    CommonHelper.showOnGantt(this.router, 'order', order.uuid);
    CommonHelper.handleOptions(order.uuid, options);
  }

  public showOnMap(
    order: Order | TelematicsOrder,
    options: HelperOptions,
  ): void {
    const vehicleUuid = OrderHelper.isTelematicsOrder(order)
      ? order.fleetUuid
      : OrderHelper.getOrderVehicle(order)?.uuid;
    if (!OrderHelper.hasShowOnMap(order) || !vehicleUuid) return;

    CommonHelper.showOnMap(this.router, vehicleUuid, this.injector, {
      orderUuid: order.uuid,
    });
    CommonHelper.handleOptions(order.uuid, options);
  }

  public delete(
    order: Order | TelematicsRoutePointOrder,
    options: HelperOptions,
  ): void {
    this.modalService
      .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
        heading: this.translateService.instant('orders.delete.heading', {
          identifier: order.identifier,
        }),
        message: 'orders.delete.message',
        confirmButtonText: 'labels.delete',
        confirmButtonColor: 'red',
        action: () => this.ordersService.deleteOrder(order.uuid),
      })
      .result.subscribe((ok) => {
        if (!ok) return;

        this.toastService.showSuccess({ message: 'orders.delete.success' });
        CommonHelper.handleOptions(order, options);
      });
  }

  public deleteOutsourcing({ identifier, uuid }: SimplifiedOrder): void {
    const I18N = 'orders.orderModal';

    this.modalService
      .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
        heading: this.translateService.instant(
          `${I18N}.outsourcingForm.wantToDeleteOutsourcing`,
          { identifier },
        ),
        confirmButtonText: 'labels.delete',
        confirmButtonColor: 'red',
        action: () => this.ordersService.deleteOutsourcing(uuid),
      })
      .result.subscribe((ok) => {
        if (!ok) return;

        this.toastService.showSuccess({
          message: `${I18N}.toasts.outsourcingDeleted`,
        });
      });
  }

  public moveToDraft(
    order: Order | TelematicsRoutePointOrder,
    options: HelperOptions,
    hasUndo = true,
  ): Observable<void> {
    return this.ordersService.moveToDraft(order.uuid).pipe(
      tap(() => {
        this.showSearchToggleToast(
          order,
          'orders.draft.movedToDraft',
          hasUndo && 'dispatch' in order
            ? this.resumeSearch(order, {}, false)
            : null,
        );
        CommonHelper.handleOptions(order, options);
      }),
    );
  }

  public resumeSearch(
    order: Order | TelematicsRoutePointOrder,
    options: HelperOptions,
    hasUndo = true,
  ): Observable<void> {
    if (!('dispatch' in order)) return EMPTY;

    return this.ordersService.dispatch(order.uuid, order.dispatch).pipe(
      tap(() => {
        this.showSearchToggleToast(
          order,
          'orders.draft.searchResumed',
          hasUndo ? this.moveToDraft(order, {}, false) : null,
        );
        CommonHelper.handleOptions(order, options);
      }),
    );
  }

  public archive(uuid: string, options: HelperOptions): void {
    this.ordersService.archive(uuid).subscribe(() => {
      this.toastService.showSuccess({
        message: 'orders.toasts.orderArchived',
      });
      CommonHelper.handleOptions(uuid, options);
    });
  }

  public getPaymentDelay(
    order: Order,
  ): { days: number; isPast: boolean; isToday: boolean } | null {
    const paymentTermISO = order.payment?.invoice?.paymentTerm;
    if (!paymentTermISO) return null;

    const paymentTerm = DateTime.fromISO(paymentTermISO).startOf('day');

    switch (order.status.value) {
      case OrderStatus.WAITING_FOR_PAYMENT:
      case OrderStatus.PARTIALLY_PAID: {
        const remainingDays = paymentTerm.diff(
          DateTime.now().startOf('day'),
          'days',
        ).days;

        return {
          days: Math.abs(remainingDays),
          isPast: remainingDays < 0,
          isToday: remainingDays === 0,
        };
      }

      case OrderStatus.PAID: {
        const paidAtISO = order.timestamps.paidAt;
        if (!paidAtISO) return null;

        const paidAt = DateTime.fromISO(paidAtISO).startOf('day');

        const remainingDays = paymentTerm.diff(paidAt, 'days').days;

        return {
          days: Math.abs(remainingDays),
          isPast: remainingDays < 0,
          isToday: remainingDays === 0,
        };
      }

      default:
        return null;
    }
  }

  public getTimeWindows(
    point: Point,
    options?: {
      businessHoursFormat?: 'short' | 'long';
      showDate?: boolean;
      onlyWeekdays?: boolean;
      separator?: string;
      styled?: boolean;
      showSameDay?: boolean;
    },
  ) {
    const {
      businessHoursFormat = 'long',
      showDate = true,
      onlyWeekdays = false,
      separator = ' · ',
      styled = false,
      showSameDay = false,
    } = options ?? {};

    const formatTimeWindow = (date: string, time: OnyxTime | OnyxTimeRange) => {
      const dateString = `${CommonHelper.isSameDay(date) && !showSameDay ? '' : `${separator}${this.datePipe.transform(date, 'date-dot')}`}`;
      return `${this.timePipe.transform(time)}${showDate ? `${styled ? `<span class="f-muted">${dateString}</span>` : dateString}` : ''}`;
    };

    const formatBusinessHours = (businessHours: BusinessHours) => [
      this.businessHoursPipe.transform(
        businessHours,
        'weekday',
        businessHoursFormat,
      ),
      ...(!onlyWeekdays
        ? [
            this.businessHoursPipe.transform(
              businessHours,
              'saturday',
              businessHoursFormat,
            ),
            this.businessHoursPipe.transform(
              businessHours,
              'sunday',
              businessHoursFormat,
            ),
          ]
        : []),
    ];

    switch (point.category) {
      case OrderPointCategory.CHECKPOINT:
      case OrderPointCategory.PICKUP: {
        const businessHours = point.businessHours;
        const isAllTime = Object.values(businessHours).every(
          (range) => range === DAY_TIME_RANGE,
        );

        return {
          label: 'labels.openingHours',
          value: isAllTime ? ['24/7'] : formatBusinessHours(businessHours),
        };
      }

      case OrderPointCategory.TRANSIT: {
        return {
          label: 'labels.fix',
          value:
            point.timeWindow.type === OrderTransitPointTimeWindowType.ALL_TIME
              ? ['24/7']
              : [
                  formatTimeWindow(
                    point.timeWindow.date,
                    point.timeWindow.time,
                  ),
                ],
        };
      }

      default: {
        if (point.timeWindows.type === OrderTimeWindowType.FIX) {
          return {
            label: 'labels.fix',
            value: [
              formatTimeWindow(
                point.timeWindows.windows[0].date,
                point.timeWindows.windows[0].time,
              ),
            ],
          };
        }

        const isInterval =
          point.timeWindows.type === OrderTimeWindowType.INTERVAL;
        return {
          label: isInterval ? 'labels.interval' : 'labels.openingHours',
          value: point.timeWindows.windows.map((timeWindow) =>
            formatTimeWindow(timeWindow.date, timeWindow.timeRange),
          ),
        };
      }
    }
  }

  public getStatusIcon(
    order: Order | OrderStatus,
    iconStyle: 'outlined' | 'filled',
  ): OnyxIconName {
    const status = !isString(order) ? order.status.value : order;
    const payment = !isString(order) ? order.payment : null;
    const hasManualProposal =
      !isString(order) && order.proposalData?.type === OrderProposalType.MANUAL;
    const startPlanningData = !isString(order)
      ? OrderHelper.getStartPlanningData(order)
      : null;

    const getVehicleSearchIcon = (
      iconStyle: 'outlined' | 'filled',
    ): OnyxIconName => {
      if (hasManualProposal) return `manual-${iconStyle}`;
      if (startPlanningData?.isWaiting) return `clock-${iconStyle}`;

      const deadlineTime = !isString(order)
        ? RouteHelper.getPointDeadlineTime(
            OrderHelper.getCurrentPoint(order.points),
          )
        : null;
      if (deadlineTime && CommonHelper.isPast(deadlineTime)) {
        return 'error-neutral-filled';
      }

      return 'signet';
    };

    const getInvoiceToIssueIcon = (
      iconStyle: 'outlined' | 'filled',
    ): OnyxIconName => {
      if (!payment) return `document-${iconStyle}`;

      const isWaitingForCollectiveInvoiceIssue =
        OrderHelper.isWaitingForCollectiveInvoiceIssue(
          payment.collectiveInvoiceIssueFrom,
        );
      if (isWaitingForCollectiveInvoiceIssue) return `clock-${iconStyle}`;

      if (payment.contractorInvoice.defaultType === InvoiceType.SELF_BILLING) {
        return `finance-${iconStyle}`;
      }

      return `document-${iconStyle}`;
    };

    const getInProgressIcon = (): OnyxIconName => {
      const DEFAULT_ICON: OnyxIconName = 'vehicle-ride-filled';
      if (isString(order) || !order.points) return DEFAULT_ICON;

      const currentPoint = OrderHelper.getCurrentPoint(order.points);
      if (
        !currentPoint ||
        currentPoint.status === OnyxMapRoutePointStatus.TO_DO
      ) {
        return DEFAULT_ICON;
      }

      const pointData = RouteHelper.getPointRouteData(
        currentPoint,
        order.points,
        this.commonHelper.nowMinute(),
      );

      return (
        RouteHelper.getPointAction(
          currentPoint,
          currentPoint.status,
          pointData?.compliance,
          true,
          this.translateService,
        )?.icon ?? DEFAULT_ICON
      );
    };

    return (
      {
        [OrderStatus.DRAFT]: `edit-${iconStyle}`,
        [OrderStatus.VEHICLE_SEARCH]: getVehicleSearchIcon(iconStyle),
        [OrderStatus.TO_ACCEPT]: `decision-${iconStyle}`,
        [OrderStatus.TO_DO]: `clock-${iconStyle}`,
        [OrderStatus.IN_PROGRESS]: getInProgressIcon(),
        [OrderStatus.COMPLETING_DOCUMENTS]: `document-${iconStyle}`,
        [OrderStatus.INVOICE_TO_ISSUE]: getInvoiceToIssueIcon(iconStyle),
        [OrderStatus.WAITING_FOR_PAYMENT]: `finance-${iconStyle}`,
        [OrderStatus.PARTIALLY_PAID]: `finance-${iconStyle}`,
        [OrderStatus.PAID]: `check-circle-${iconStyle}`,
        [OrderStatus.ARCHIVED]: `archives-${iconStyle}`,
        [OrderStatus.CANCELED]: `clear-${iconStyle}`,
      } as const
    )[status];
  }

  public duplicate(
    order: Order | TelematicsRoutePointOrder,
    options?: HelperOptions,
  ): void {
    this.router.navigateByUrl(
      OrdersRoute.ADD_ORDER,
      NavigationHelper.withOrigin(this.router, {
        state: { [OrdersRouterStateKey.TEMPLATE]: order },
      }),
    );
    options && CommonHelper.handleOptions(order.uuid, options);
  }

  private showSearchToggleToast(
    order: Order | TelematicsRoutePointOrder,
    message: string,
    undo$: Observable<void> | null,
  ): void {
    const BUTTON_STYLE = {
      color: 'black',
      variant: 'ghost',
      size: 24,
    } as const;

    this.toastService.showSuccess({
      message,
      description: this.translateService.instant(
        'orders.draft.toastDescription',
        { identifier: order.identifier },
      ),
      buttons: [
        {
          text: 'labels.show',
          callback: () => this.openModal(order.uuid),
          ...BUTTON_STYLE,
        },
        ...(undo$
          ? [
              {
                text: 'labels.undo',
                callback: () => undo$.subscribe(),
                ...BUTTON_STYLE,
              },
            ]
          : []),
      ],
    });
  }

  public static getPointAddress(point: OrderPoint): OnyxAddress {
    return 'address' in point ? point.address : point.startAddress;
  }

  public static getFirstLoadingPoint(points: OrderPoint[]): OrderLoadingPoint {
    return points.find(
      (point) => point.category === OrderPointCategory.LOADING,
    )!;
  }

  public static getLastUnloadingPoint(
    points: OrderPoint[],
  ): OrderUnloadingPoint {
    return points
      .reverse()
      .find((point) => point.category === OrderPointCategory.UNLOADING)!;
  }

  public static hasLoadedSemiTrailer(order: Order): boolean {
    const { uuid, registrationNumber } =
      order.basicInformation.loadedSemiTrailer ?? {};
    return !!(uuid || registrationNumber);
  }

  public static hasShowOnMap(data: Order | TelematicsOrder): boolean {
    const isTelematics = OrderHelper.isTelematicsOrder(data);
    if (isTelematics) return data.fleetUuid != null;

    const order = isTelematics ? data.order : data;
    if (!order) return false;

    if (OrderHelper.isInactive(order)) return false;

    const vehicle = OrderHelper.getOrderVehicle(order as Order);
    return vehicle != null;
  }

  public static hasApprovedVehicle(
    order: Order | TelematicsRoutePointOrder | OrderStatus | null,
  ): boolean {
    if (!order) return false;

    const status = isString(order) ? order : order.status.value;
    return !OrderHelper.isDraft(status) && !OrderHelper.isSearching(status);
  }

  public static getRoutePointStatusByOrderPointStatus(
    status: OrderPointStatus,
    order: Order | TelematicsRoutePointOrder | null,
  ): TelematicsRoutePointStatus {
    const orderStatus = order?.status.value ?? null;

    if (orderStatus && !OrderHelper.hasApprovedVehicle(orderStatus)) {
      return TelematicsRoutePointStatus.PLANNED;
    } else if (
      OrderHelper.isCanceled(orderStatus) ||
      status === OrderPointStatus.CANCELED
    ) {
      return TelematicsRoutePointStatus.CANCELED;
    } else if (
      OrderHelper.isFinished(orderStatus) ||
      status === OrderPointStatus.COMPLETED
    ) {
      return TelematicsRoutePointStatus.COMPLETED;
    }

    return TelematicsRoutePointStatus.ASSIGNED;
  }

  public static isPending(order: Order | OrderStatus): boolean {
    const status = isString(order) ? order : order.status.value;
    return [
      OrderStatus.DRAFT,
      OrderStatus.VEHICLE_SEARCH,
      OrderStatus.TO_ACCEPT,
      OrderStatus.TO_DO,
    ].includes(status);
  }

  public static isDraft(
    order: Order | TelematicsRoutePointOrder | OrderStatus,
  ): boolean {
    const status = isString(order) ? order : order.status.value;
    return status === OrderStatus.DRAFT;
  }

  public static isPausedDraft(
    order: Order | TelematicsRoutePointOrder,
  ): boolean {
    return (
      OrderHelper.isDraft(order) &&
      'dispatch' in order &&
      order.dispatch !== OrderDispatch.DRAFT
    );
  }

  public static getEditLock(order: Order | TelematicsRoutePointOrder): {
    disabled: boolean;
    tooltip: string;
  } {
    return {
      disabled: OrderHelper.isSearching(order),
      tooltip: 'orders.draft.editBlocked',
    };
  }

  public static isSearching(
    order: Order | TelematicsRoutePointOrder | OrderStatus,
  ): boolean {
    const status = isString(order) ? order : order.status.value;
    return [OrderStatus.VEHICLE_SEARCH, OrderStatus.TO_ACCEPT].includes(status);
  }

  public static hasFleetDispatch(
    order: Order | TelematicsRoutePointOrder,
  ): boolean {
    return (
      !('dispatch' in order) ||
      [OrderDispatch.FLEET, OrderDispatch.FLEET_GLOBAL].includes(order.dispatch)
    );
  }

  public static isActive(order: Order | OrderStatus): boolean {
    const status = isString(order) ? order : order.status.value;
    return [OrderStatus.TO_DO, OrderStatus.IN_PROGRESS].includes(status);
  }

  public static isFinished(
    order: Order | TelematicsRoutePointOrder | OrderStatus | null,
  ): boolean {
    if (!order) return false;

    const status = isString(order) ? order : order.status.value;
    return !this.isPending(status) && status !== OrderStatus.IN_PROGRESS;
  }

  public static isCanceled(order: Order | OrderStatus | null): boolean {
    if (!order) return false;

    const status = isString(order) ? order : order.status.value;
    return status === OrderStatus.CANCELED;
  }

  public static isArchived(order: Order | OrderStatus): boolean {
    const status = isString(order) ? order : order.status.value;
    return status === OrderStatus.ARCHIVED;
  }

  public static isInactive(
    order: Order | TelematicsRoutePointOrder | OrderStatus,
  ): boolean {
    const status = isString(order) ? order : order.status.value;
    return [OrderStatus.ARCHIVED, OrderStatus.CANCELED].includes(status);
  }

  public static getDriverReportRawFilesData(
    report: InProgressReport | CompletedReport | null,
  ): DriverReportModalFileData[] {
    if (!report) return [];

    const I18N = 'orders.driverReportModal';

    const cmrScan = 'cmrScan' in report ? report.cmrScan : null;
    const driversSignature =
      'driversSignature' in report ? report.driversSignature : null;
    const partnersSignature =
      'partnersSignature' in report ? report.partnersSignature : null;

    return [
      {
        files: cmrScan?.files ?? [],
        images: [],
        title: `${I18N}.cmrScan`,
        note: cmrScan?.note ?? undefined,
      },
      ...report.attachments.map((attachment) => ({
        files: attachment.files,
        images: [],
        title:
          attachment.name ??
          `${DictionaryCode.ATTACHMENT_TYPE}.${attachment.type}`,
        note: attachment.note ?? undefined,
      })),
      {
        files: partnersSignature ? [partnersSignature] : [],
        images: [],
        title: `${I18N}.partnerSignature`,
      },
      {
        files: driversSignature ? [driversSignature] : [],
        images: [],
        title: `${I18N}.driverSignature`,
      },
    ];
  }

  public static getCrewData<Key extends keyof OrderCrew>(
    order: Order,
    key: Key,
  ): OrderCrew[Key] {
    return order.assignedData?.[key] ?? order.proposalData?.[key] ?? null;
  }

  public static getOrderVehicle(order: Order): SimplifiedFleet | null {
    return OrderHelper.getCrewData(order, 'vehicle');
  }

  public static getEngineProposal(order: Order): OrderProposal | null {
    const proposal = order.proposalData;
    return proposal?.type === OrderProposalType.ENGINE ? proposal : null;
  }

  public static getManualProposal(order: Order): OrderProposal | null {
    const proposal = order.proposalData;
    return proposal?.type === OrderProposalType.MANUAL ? proposal : null;
  }

  public static isManualProposalCalculating(order: Order): boolean {
    const proposal = OrderHelper.getManualProposal(order);
    return !!proposal && proposal.result == null;
  }

  public static getOrderDrivers(order: Order): SimplifiedDrivers {
    const crew = DriverHelper.compactDrivers(order.assignedData).length
      ? order.assignedData
      : order.proposalData;

    return {
      primaryDriver: crew?.primaryDriver ?? null,
      secondaryDriver: crew?.secondaryDriver ?? null,
    };
  }

  public static getCurrentServiceTime(
    routePoint: OnyxOptional<TelematicsRoutePoint>,
  ): number | null {
    if (!routePoint) return null;

    const point = RouteHelper.getPointValue(routePoint);
    if (!point || point.status === OrderPointStatus.TO_DO) return null;

    return point.serviceTime ?? null;
  }

  public static getEmptyDistance(
    order: Order,
  ): { value: number; percentage: number } | null {
    const emptyDistance = this.getEmptyDistanceValue(order);
    if (!emptyDistance) return null;

    return {
      value: emptyDistance,
      percentage: (emptyDistance / this.getTotalDistance(order)) * 100,
    };
  }

  public static getDistance(order: Order): number {
    return order.route.summary.distance;
  }

  public static getTotalDistance(order: Order): number {
    const emptyDistance = OrderHelper.getEmptyDistanceValue(order);
    const distance = OrderHelper.getDistance(order);

    return (emptyDistance ?? 0) + distance;
  }

  public static getRemainingDistance(order: Order): number {
    return sumBy(order.points, (point) => {
      if (point.status !== OrderPointStatus.TO_DO) return 0;
      return (
        point.estimations?.remainingDistance ?? point.route.summary.distance
      );
    });
  }

  public static getEmptyDistanceDuration(order: Order): number | null {
    return (
      (order.assignedData ?? order.proposalData?.result)
        ?.emptyDistanceDuration ?? null
    );
  }

  public static getDuration(order: Order): { order: number; route: number } {
    const { firstPoint, lastPoint } = OrderHelper.getDurationData(order);
    const start = DateTime.fromISO(firstPoint.timestamp);
    const end = DateTime.fromISO(lastPoint.timestamp);

    return {
      order: end.diff(start).as('seconds'),
      route: order.route.summary.duration,
    };
  }

  public static getTotalDuration(order: Order): number {
    const emptyDistanceDuration = OrderHelper.getEmptyDistanceDuration(order);
    const duration = OrderHelper.getDuration(order);

    return (emptyDistanceDuration ?? 0) + duration.route;
  }

  public static getRemainingDuration(points: OrderPoint[] | Order): number {
    points = isArray(points) ? points : points.points;
    return sumBy(points, (point) => {
      if (point.status !== OrderPointStatus.TO_DO) return 0;

      const { distance, duration } = point.route.summary;
      const remainingDistance =
        point.estimations?.remainingDistance ?? distance;
      const remainingDuration = duration * (remainingDistance / distance);

      return remainingDuration;
    });
  }

  public static getDurationData(order: Order | OrderPoint[]): {
    firstPoint: OrderPointDurationData;
    lastPoint: OrderPointDurationData;
  } {
    const points = isArray(order) ? order : order.points;

    const parseTimestamp = (
      timestamp: string,
      isEstimated: boolean,
    ): OrderPointDurationData => {
      const [date, time] = timestamp.split('T');
      return {
        timestamp,
        date,
        time: time as OnyxTime,
        isEstimated,
      };
    };

    const firstPoint = ((): OrderPointDurationData => {
      const firstPoint = points[0];
      const startedAt = firstPoint.timestamps.startedAt;
      if (startedAt) return parseTimestamp(startedAt, false);

      const estimatedArrivalTime = firstPoint.estimations?.arrivalTime;
      if (estimatedArrivalTime) {
        return parseTimestamp(estimatedArrivalTime, true);
      }

      const deadline = DeadlineHelper.getPointDeadline(firstPoint)!.arrival;
      return parseTimestamp(
        deadline.toISO({ suppressMilliseconds: true })!,
        true,
      );
    })();

    const lastPoint = ((): OrderPointDurationData => {
      const lastPoint = points.at(-1)!;
      const completedAt = lastPoint.timestamps.completedAt;
      if (completedAt) return parseTimestamp(completedAt, false);

      const estimatedDepartureTime = lastPoint.estimations?.departureTime;
      if (estimatedDepartureTime) {
        return parseTimestamp(estimatedDepartureTime, true);
      }

      const deadline = points
        .toReversed()
        .map((point) => DeadlineHelper.getPointDeadline(point)?.departure)
        .find(Boolean)!;
      return parseTimestamp(
        deadline.toISO({ suppressMilliseconds: true })!,
        true,
      );
    })();

    return { firstPoint, lastPoint };
  }

  public static getKmRate(order: Order): OnyxAmount {
    return AmountHelper.divide(
      order.price,
      OrderHelper.getTotalDistance(order) / 1000,
    );
  }

  public static getDailyRate(order: Order): OnyxAmount {
    const emptyDistanceDuration = OrderHelper.getEmptyDistanceDuration(order);
    const { order: duration } = OrderHelper.getDuration(order);

    const days = Math.ceil(
      Duration.fromObject({
        seconds: (emptyDistanceDuration ?? 0) + duration,
      }).as('days'),
    );

    return AmountHelper.divide(order.price, days);
  }

  public static getPaymentDetails(payment: OrderPayment) {
    const invoice = payment?.invoice;
    if (!invoice) return null;

    const invoiceNetPrice = invoice.net?.value ?? 0;
    const invoiceVatPrice = invoice.vat?.value ?? 0;

    const totalNetPayments = sumBy(payment.history, (p) => p.net?.value ?? 0);
    const totalVatPayments = sumBy(payment.history, (p) => p.vat?.value ?? 0);

    const remainingNet = invoiceNetPrice - totalNetPayments;
    const remainingVat = invoiceVatPrice - totalVatPayments;

    return {
      invoiceNetAmount: invoice.net,
      invoiceVatAmount: invoice.vat,
      invoiceNetPrice,
      invoiceVatPrice,
      totalNetPayments,
      totalVatPayments,
      remainingNet,
      remainingVat,
    };
  }

  public static getPaidPercentage(
    order: Order,
    decimalPlaces = 2,
  ): number | null {
    const { status, payment } = order;
    if (status.value !== OrderStatus.PARTIALLY_PAID) return null;

    const invoice = payment.invoice;
    if (!invoice) return null;

    const decimalRate = 10 ** decimalPlaces;

    const netPayments = payment.history
      .map((payment) => payment.net)
      .filter((net) => net != null);
    const netSum = netPayments
      .slice(1)
      .reduce(
        (payment, total) => AmountHelper.add(payment, total),
        netPayments[0],
      );
    if (!invoice.vat.value) {
      return Math.floor(
        ((netSum?.value ?? 0) / invoice.net.value) * decimalRate,
      );
    }

    const vatPayments = payment.history
      .map((payment) => payment.vat)
      .filter((vat) => vat != null);
    const vatSum = vatPayments
      .slice(1)
      .reduce(
        (payment, total) => AmountHelper.add(payment, total),
        vatPayments[0],
      );

    if (invoice.net.currency === invoice.vat.currency) {
      const sum =
        netSum && vatSum
          ? AmountHelper.add(netSum, vatSum)
          : (netSum ?? vatSum);
      const total = AmountHelper.add(invoice.net, invoice.vat);

      return Math.floor((sum.value / total.value) * decimalRate);
    }

    const euroSum = (netSum?.euroValue ?? 0) + (vatSum?.euroValue ?? 0);
    const euroTotal = invoice.net.euroValue + invoice.vat.euroValue;

    return Math.floor((euroSum / euroTotal) * decimalRate);
  }

  public static getSettlementData(
    order: Order,
    variant: 'order' | 'outsourcing' = 'order',
  ): OrderSettlementData[] {
    const { defaultCurrencyValue, defaultCurrency, value, currency } =
      order.price;
    const outsourcingData = order.outsourcingData;
    const isOutsourcing = variant === 'outsourcing';

    const vatRate = (() =>
      isOutsourcing && outsourcingData
        ? outsourcingData.vatRate
        : order.basicInformation.vatRate)();
    const vatRateValue = (() => {
      if (isOutsourcing && outsourcingData) {
        const outsourcingVatRate = outsourcingData.vatRate;
        return CommonHelper.isNumeric(outsourcingVatRate)
          ? +outsourcingVatRate
          : null;
      }

      return CommonHelper.isNumeric(vatRate) ? +vatRate : null;
    })();

    const calculateSettlementEntry = (
      netValue: number,
      currency: string,
    ): OrderSettlementData => {
      const vatAmount = vatRateValue ? (netValue * vatRateValue) / 100 : 0;
      const grossValue = netValue + vatAmount;

      return {
        net: { value: netValue, currency },
        gross: { value: grossValue, currency },
        vatRate: { value: vatAmount, currency, vatRate: vatRate },
      };
    };

    if (isOutsourcing && outsourcingData) {
      const { value, currency } = outsourcingData.rate;
      return [calculateSettlementEntry(value, currency)];
    }

    return [
      calculateSettlementEntry(defaultCurrencyValue, defaultCurrency),
      ...(defaultCurrency !== currency
        ? [calculateSettlementEntry(value, currency)]
        : []),
    ];
  }

  public static getStartPlanningData(order: Order) {
    const point = order.points.find(
      (point) =>
        point.category === OrderPointCategory.LOADING ||
        ('includeInOrder' in point && point.includeInOrder),
    );
    if (!point) return null;

    const arrivalDeadline = DeadlineHelper.getPointDeadline(point)?.arrival;
    if (!arrivalDeadline) return null;

    const isWaiting = arrivalDeadline.diffNow('days').days > 7;
    const deadline = arrivalDeadline.minus({ days: 7 });

    return { isWaiting, deadline };
  }

  public static hasEmptyDistanceError(
    emptyDistance: number,
    distance: number,
    authStore: AuthStore,
  ): boolean {
    const emptyKilometers = emptyDistance / 1_000;
    const engineConfig = authStore.engineConfig()!.emptyDistance;

    const invalidDistanceStrategy =
      emptyKilometers > (engineConfig.distanceThreshold ?? Infinity);
    const invalidPercentageStrategy =
      emptyKilometers >
      ((distance / 1000) * (engineConfig.percentageThreshold ?? Infinity)) /
        100;

    switch (engineConfig.strategy) {
      case EmptyDistanceStrategy.VALUE:
        return invalidDistanceStrategy;
      case EmptyDistanceStrategy.PERCENTAGE:
        return invalidPercentageStrategy;
      case EmptyDistanceStrategy.PERCENTAGE_AND_VALUE:
        return invalidPercentageStrategy && invalidDistanceStrategy;
      case EmptyDistanceStrategy.PERCENTAGE_OR_VALUE:
        return invalidPercentageStrategy || invalidDistanceStrategy;
      default:
        return false;
    }
  }

  public static hasEdit(
    order: Order | TelematicsRoutePointOrder | OrderStatus,
  ): boolean {
    const status = isString(order) ? order : order.status.value;
    return !OrderHelper.isInactive(status);
  }

  public static hasPlannedSwap(
    order: Order | TelematicsRoutePointOrder,
  ): boolean {
    return 'relatedFleetSwap' in order && !!order.relatedFleetSwap;
  }

  public static isTelematicsOrder(
    order: Order | TelematicsOrder,
  ): order is TelematicsOrder {
    return 'order' in order;
  }

  public static isOrderType(order: SimplifiedOrder | Order): order is Order {
    return 'price' in order;
  }

  public static getCurrentPoint(points: OrderPoint[]): OrderPoint | null {
    return (
      points.find(
        (point) =>
          ![OrderPointStatus.COMPLETED, OrderPointStatus.CANCELED].includes(
            point.status,
          ),
      ) ?? null
    );
  }

  public static getRoute(order: Order | Point[]): OnyxRoute {
    const points = isArray(order) ? order : order.points;
    const routePoints = points.map((point) => point.route);

    const summary = !isArray(order)
      ? routePoints
          .map((point) => point.summary)
          .slice(1)
          .reduce(
            (result, summary) => ({
              ...result,
              distance: result.distance + summary.distance,
              duration: result.duration + summary.duration,
              countries: uniq([...result.countries, ...summary.countries]),
              adBlueConsumption:
                result.adBlueConsumption + summary.adBlueConsumption,
              adBlueCost:
                result.adBlueCost && summary.adBlueCost
                  ? AmountHelper.add(result.adBlueCost, summary.adBlueCost)
                  : summary.adBlueCost,
              fuelConsumption: result.fuelConsumption + summary.fuelConsumption,
              fuelCost:
                result.fuelCost && summary.fuelCost
                  ? AmountHelper.add(result.fuelCost, summary.fuelCost)
                  : summary.fuelCost,
              totalFuelCost:
                result.totalFuelCost && summary.totalFuelCost
                  ? AmountHelper.add(
                      result.totalFuelCost,
                      summary.totalFuelCost,
                    )
                  : summary.totalFuelCost,
              tollsCost: AmountHelper.add(result.tollsCost, summary.tollsCost),
              tolls: chain([...result.tolls, ...summary.tolls])
                .groupBy((toll) => `${toll.countryCode}-${toll.systemName}`)
                .map((group) => {
                  const { countryCode, systemName } = group[0];
                  return {
                    countryCode,
                    systemName,
                    cost: group
                      .slice(1)
                      .reduce(
                        (sum, toll) => AmountHelper.add(sum, toll.cost),
                        group[0].cost,
                      ),
                  };
                })
                .value(),
              totalCost: AmountHelper.add(result.totalCost, summary.totalCost),
            }),
            routePoints[0].summary,
          )
      : routePoints[0].summary;

    return { points: routePoints, summary, handle: null };
  }

  public static getGoodSizeDecimalPlaces(
    sizeType: OnyxOptional<GoodsSizeType>,
  ): number {
    if (!sizeType) return 0;

    return {
      [GoodsSizeType.DIMENSIONS]: 0,
      [GoodsSizeType.VOLUME]: 3,
      [GoodsSizeType.LOADING_METERS]: 2,
    }[sizeType];
  }

  public static getGoodUnit(sizeType: OnyxOptional<GoodsSizeType>): string {
    if (!sizeType) return 'kg';

    return {
      [GoodsSizeType.DIMENSIONS]: 'units.pieces',
      [GoodsSizeType.VOLUME]: 'm³',
      [GoodsSizeType.LOADING_METERS]: 'LDM',
    }[sizeType];
  }

  public static getGoodQuantity(
    good: OnyxOptional<OrderLoadingGood | OrderLoadingPointGoodForm>,
  ): number | null {
    if (!good) return null;

    const size = good.size;
    const type = size?.type;
    if (!type) return good.totalWeight;

    switch (type) {
      case GoodsSizeType.DIMENSIONS:
        return good.quantity;

      case GoodsSizeType.LOADING_METERS:
        return size.loadingMeters;

      case GoodsSizeType.VOLUME:
        return size.volume;
    }
  }

  public static getReferenceItems(order: Order): OrderReferenceItem[] {
    const orderReferenceNumber = order.basicInformation.referenceNumber;
    const points = order.points;

    return chain(points)
      .reduce((result: OrderReferenceItem[], point) => {
        if ('referenceNumber' in point && point.referenceNumber) {
          result.push({
            category: 'point',
            type: point.type,
            typeIndex:
              points
                .filter((orderPoint) => orderPoint.type === point.type)
                .indexOf(point) + 1,
            referenceNumber: point.referenceNumber,
          });
        }

        if ('goods' in point && point.goods?.length) {
          for (const good of point.goods) {
            if ('referenceNumber' in good && good.referenceNumber) {
              result.push({
                category: 'good',
                names: good.unit.names,
                referenceNumber: good.referenceNumber,
              });
            }
          }
        }

        return result;
      }, [] as OrderReferenceItem[])
      .orderBy((item) => item.category, ['desc'])
      .thru((items) =>
        orderReferenceNumber
          ? [
              {
                category: 'order' as const,
                referenceNumber: orderReferenceNumber,
              },
              ...items,
            ]
          : items,
      )
      .value();
  }

  public static getRoutePoints(
    order: Order,
    primaryDriverDelegationUuid: string | null,
    secondaryDriverDelegationUuid: string | null,
  ): TelematicsRoutePoint[] {
    const routePointOrder: TelematicsRoutePoint['order'] = {
      uuid: order.uuid,
      identifier: order.identifier,
      author: order.author,
      status: order.status,
      proposalData: order.proposalData,
      basicInformation: order.basicInformation,
      sender: order.basicInformation.sender,
      isDedicated: order.parameters.isDedicated,
      sentNotificationNumber: order.parameters.sentNotificationNumber,
      pointsCount: order.points.length,
      route: order.route,
    };

    return TelematicsRoutePointFactory.fromDto(
      order.points.map((point): TelematicsOrderRoutePointDto => ({
        order: routePointOrder,
        point,
        technicalPoint: null,
        route: point.route,
        status: OrderHelper.getRoutePointStatusByOrderPointStatus(
          point.status,
          order,
        ),
        trailerUuid: null,
        primaryDriverDelegationUuid,
        secondaryDriverDelegationUuid,
      })),
    );
  }

  public static hasRequiredDocuments(shippingType: ShippingType) {
    const { emails, internalPortal, traditionalMail } = shippingType;
    return (emails?.length ?? 0) > 0 || !!internalPortal || !!traditionalMail;
  }

  public static hasCollectiveInvoice(order: Order): boolean {
    return (
      order?.payment.contractorInvoice.defaultType === InvoiceType.COLLECTIVE
    );
  }

  public static isWaitingForCollectiveInvoiceIssue(
    collectiveInvoiceIssueFrom: string | null,
  ): boolean {
    const issueFrom = collectiveInvoiceIssueFrom
      ? DateTime.fromISO(collectiveInvoiceIssueFrom).startOf('day')
      : null;

    return !!(
      issueFrom &&
      issueFrom.diff(DateTime.now().startOf('day'), 'days').days > 0
    );
  }

  public static getMissingAssignment(order: Order): {
    type: OrderMissingAssignmentType;
    label: string;
    icon: OnyxIconName;
  } | null {
    if (
      !order.assignedData ||
      order.outsourcingData ||
      OrderHelper.isFinished(order)
    ) {
      return null;
    }

    const I18N = 'orders.ordersList.cells';

    const { loadedSemiTrailer } = order.basicInformation;
    const { vehicle, primaryDriver, secondaryDriver } = order.assignedData;
    const trailer =
      order.assignedData.trailer ||
      loadedSemiTrailer.data ||
      loadedSemiTrailer.registrationNumber;

    const missingAssignmentTypes = [
      {
        variable: !vehicle && !trailer,
        data: {
          type: OrderMissingAssignmentType.GOODS_WITHOUT_VEHICLE,
          label: `${I18N}.goodsWithoutVehicle`,
          icon: 'good-filled' as const satisfies OnyxIconName,
        },
      },
      {
        variable: !vehicle && !!trailer,
        data: {
          type: OrderMissingAssignmentType.LOADED_TRAILER_WITHOUT_VEHICLE,
          label: `${I18N}.loadedTrailerWithoutVehicle`,
          icon: 'vehicle-disconnected-filled' as const satisfies OnyxIconName,
        },
      },
      {
        variable: !primaryDriver && !secondaryDriver,
        data: {
          type: OrderMissingAssignmentType.ORDER_WITHOUT_DRIVER,
          label: `${I18N}.orderWithoutDriver`,
          icon: 'no-user-filled' as const satisfies OnyxIconName,
        },
      },
    ];

    return missingAssignmentTypes.find((item) => item.variable)?.data ?? null;
  }

  public static createCategoryFilter(categories: OrderCategory[]): OnyxFilter {
    return {
      key: 'category',
      operator: OnyxFilterOperator.IN,
      value: categories,
    };
  }

  public static createStatusFilter(statuses: OrderStatus[]): OnyxFilter {
    return { key: 'status', operator: OnyxFilterOperator.IN, value: statuses };
  }

  private static getEmptyDistanceValue(order: Order): number | null {
    return (
      (order.assignedData ?? order.proposalData?.result)?.emptyDistance ?? null
    );
  }
}
